import { Button, InputNumber, InputCurrency } from "@sellar-io/components";
import {
  SupplierRelationshipDiscountUpdateRequestType,
  SupplierRelationshipDiscountListResponseType,
} from "@sellar-io/api-v2/types";
import { useForm, useWatch } from "react-hook-form";
import { Alert } from "../../../components/alert/Alert";
import {
  InputLabel,
  InputContainer,
  InputSelect,
  InputOption,
} from "@sellar-io/components";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../components/modal/Dialog";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import {
  formatPriceApiToInput,
  formatPriceInputToApi,
} from "../../../hooks/usePriceFormat";
import { useSupplierRelationshipDiscountUpdate } from "../../../hooks/api/v2/supplierRelationshipDiscount/useSupplierRelationshipDiscountUpdate";
import { useSupplierRelationshipDiscountDelete } from "../../../hooks/api/v2/supplierRelationshipDiscount/useSupplierRelationshipDiscountDelete";
import { useEffect } from "react";

export const BusinessConnectionDiscountUpdateDialog = ({
  active,
  editingConnectionDiscount,
  onDismiss,
  connectionId,
}: {
  active: boolean;
  editingConnectionDiscount: SupplierRelationshipDiscountListResponseType[number];
  onDismiss: () => void;
  connectionId: number;
}) => {
  const { reset, ...formHook } =
    useForm<SupplierRelationshipDiscountUpdateRequestType>({
      shouldUseNativeValidation: true,
    });

  const { type } = useWatch({
    control: formHook.control,
  });

  useEffect(() => {
    // populate form with existing custom discount data
    if (editingConnectionDiscount) {
      reset({
        ...editingConnectionDiscount,
        discountAmount:
          editingConnectionDiscount.type === "flat"
            ? parseFloat(
                formatPriceApiToInput(editingConnectionDiscount.discountAmount),
              )
            : editingConnectionDiscount.discountAmount,
      });
    }
  }, [editingConnectionDiscount, reset]);

  const connectionDiscountUpdateMutation =
    useSupplierRelationshipDiscountUpdate(editingConnectionDiscount.id);

  const connectionDiscountDeleteMutation =
    useSupplierRelationshipDiscountDelete(editingConnectionDiscount.id);

  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        title={"Update discount"}
        description={
          "Discounts will be applied to the product item prices of the price tier you have assigned to this customer."
        }
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
      />

      <form
        onSubmit={formHook.handleSubmit((data) =>
          connectionDiscountUpdateMutation.mutate(
            {
              type: data.type,
              itemType: data.itemType,
              SupplierRetailerConnectionId: connectionId,
              discountAmount:
                data.type === "flat"
                  ? (formatPriceInputToApi(data.discountAmount) ?? 0)
                  : data.discountAmount,
            },
            {
              onSuccess: () => onDismiss(),
            },
          ),
        )}
      >
        <DialogContent>
          {connectionDiscountUpdateMutation.isError ? (
            <Alert
              type={"error"}
              message={
                apiV2AssertError(connectionDiscountUpdateMutation.error)
                  ?.response?.data.message ?? "Unknown error occurred."
              }
            />
          ) : null}

          <InputLabel>Discount type</InputLabel>
          <InputContainer>
            <InputSelect {...formHook.register("type", { required: true })}>
              <InputOption value={"flat"}>Flat</InputOption>
              <InputOption value={"percentage"}>Percentage</InputOption>
            </InputSelect>
          </InputContainer>

          <InputLabel>
            Discount amount ({type === "flat" ? "£" : "%"})
          </InputLabel>
          <InputContainer>
            {type === "flat" ? (
              <InputCurrency
                showPrefix
                allowNegative
                {...formHook.register("discountAmount", {
                  required: true,
                  min: 0,
                  valueAsNumber: true,
                })}
              />
            ) : (
              <InputNumber
                {...formHook.register("discountAmount", {
                  required: true,
                  min: 0,
                  valueAsNumber: true,
                })}
                step={"0.01"}
              />
            )}
          </InputContainer>
          <InputLabel>Container type</InputLabel>
          <InputContainer>
            <InputSelect {...formHook.register("itemType", { required: true })}>
              <InputOption value={"any"}>Any</InputOption>
              <InputOption value={"bottle"}>Bottle</InputOption>
              <InputOption value={"can"}>Can</InputOption>
              <InputOption value={"cask"}>Cask</InputOption>
              <InputOption value={"keg"}>Keg</InputOption>
            </InputSelect>
          </InputContainer>
        </DialogContent>
        <DialogCTA>
          <Button
            type="button"
            color={"red"}
            onClick={() =>
              connectionDiscountDeleteMutation.mutate(undefined, {
                onSuccess: () => onDismiss(),
              })
            }
          >
            Remove
          </Button>

          <Button
            loading={connectionDiscountUpdateMutation.isPending}
            type="submit"
          >
            Update
          </Button>
        </DialogCTA>
      </form>
    </Dialog>
  );
};
